import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { wrsCentral } from "../api.js";
import LoadingAbortPaper from "../components/Reusable/LoadingAbortPaper";
import InternalLink from "../components/Reusable/InternalLink";
import ViewAsBanner from "../components/ViewAsBanner";
import ViewAsChooser from "../components/ViewAsChooser";
import {
  ViewAsContext,
  clearViewAs,
  readViewAs,
  resetViewAs,
  writeViewAs,
} from "../viewAs";

const NOT_SET_UP = "This user has not yet been setup for WRS Central access";
const NOT_ALLOWED = "User is not allowed into WRS Central";

const LoginGuard = () => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently, user } =
    useAuth0();
  const [loadError, setLoadError] = useState(null);
  const [token, setToken] = useState(null);
  // The signed-in user's own /central/me, always fetched without view-as. It is
  // what refreshes their permissions from Auth0, and every decision about
  // whether they may view as someone is made on it -- never on the target's.
  const [ownMe, setOwnMe] = useState(null);
  const [choice, setChoice] = useState(readViewAs);
  // { api, me } for the user being viewed as, once opened.
  const [viewed, setViewed] = useState(null);
  const [viewAsError, setViewAsError] = useState(null);
  const navigate = useNavigate();

  const ownApi = useMemo(() => (token ? wrsCentral(token) : null), [token]);
  const sub = user?.sub;
  const viewAsTarget = Number.isInteger(choice?.target) ? choice.target : null;
  const canViewAs = !!ownMe?.permissions?.includes("central:impersonate");

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate("/");
      return;
    }

    if (!isAuthenticated || isLoading || error || loadError) return;

    if (!token) {
      getAccessTokenSilently()
        .then((t) => setToken(t))
        .catch((err) => setLoadError(err.message));
      return;
    }

    if (!ownMe) {
      ownApi
        .getMe()
        .then((newMe) => setOwnMe(newMe))
        .catch((err) => setLoadError(err.message));
    }
  }, [
    isAuthenticated,
    isLoading,
    error,
    loadError,
    getAccessTokenSilently,
    token,
    ownApi,
    ownMe,
    navigate,
  ]);

  // A choice belongs to whoever made it. One from a /view-as link arrives
  // unclaimed; one left in the tab by somebody else is dropped.
  useEffect(() => {
    if (!choice || !sub) return;
    if (choice.actorSub === null) {
      const bound = { ...choice, actorSub: sub };
      writeViewAs(bound);
      setChoice(bound);
    } else if (choice.actorSub !== sub) {
      clearViewAs();
      setChoice(null);
    }
  }, [choice, sub]);

  useEffect(() => {
    if (!token || !canViewAs || viewAsTarget === null || viewed) return;
    if (choice.actorSub !== sub) return;

    const api = wrsCentral(token, viewAsTarget);
    api
      .getMe()
      .then((me) => setViewed({ api, me }))
      .catch((err) => {
        // Back to the chooser, saying why, rather than stranding the user on a
        // dead tab they can only escape by guessing to log out.
        clearViewAs();
        setChoice(null);
        setViewAsError(err.message);
      });
  }, [token, canViewAs, viewAsTarget, viewed, choice, sub]);

  if (isLoading) {
    return <div>Auth0 Loading...</div>;
  }

  if (error) {
    return <div>Auth0 Error: {error.message}</div>;
  }

  if (loadError) {
    return <div>Loading Error: {loadError}</div>;
  }

  if (!ownApi || !ownMe || (choice && choice.actorSub !== sub)) {
    return <div>WRS Central is Loading...</div>;
  }

  if (ownMe.error) {
    return (
      <LoadingAbortPaper>
        <Typography>{ownMe.error}</Typography>
      </LoadingAbortPaper>
    );
  }

  const canLogin = ownMe.permissions.includes("central:login");

  if (!canLogin && !canViewAs) {
    return <div>{NOT_ALLOWED}</div>;
  }

  // Typically a staff member whose Central session is a rep account.
  if (viewAsTarget !== null && !canViewAs) {
    return (
      <LoadingAbortPaper>
        <Stack spacing={2}>
          <Typography>
            You are signed in as {ownMe.email}, which cannot view WRS Central as
            other users. Log out and sign in with your staff account to use that
            link.
          </Typography>
          <Button onClick={resetViewAs}>Continue as myself</Button>
          <InternalLink to="/logout">Log out</InternalLink>
        </Stack>
      </LoadingAbortPaper>
    );
  }

  if (viewAsTarget !== null) {
    if (!viewed) {
      return <div>Opening WRS Central as the selected user...</div>;
    }

    const { api, me } = viewed;
    // What the user would see themselves, inside the view-as chrome so Exit is
    // always within reach.
    let blocked = null;
    if (!me.permissions.includes("central:login")) blocked = NOT_ALLOWED;
    else if (!me.rep_id) blocked = NOT_SET_UP;

    return (
      <ViewAsContext.Provider value={{ readOnly: true, canViewAs, me }}>
        {blocked ? (
          <>
            <ViewAsBanner />
            <LoadingAbortPaper>
              <Typography>{blocked}</Typography>
            </LoadingAbortPaper>
          </>
        ) : (
          <Outlet context={{ api, me }} />
        )}
      </ViewAsContext.Provider>
    );
  }

  if (canViewAs && choice?.target !== "self") {
    return (
      <ViewAsChooser
        api={ownApi}
        ownMe={ownMe}
        canContinueAsSelf={canLogin && !!ownMe.rep_id}
        error={viewAsError}
        onChoose={(target) => {
          const next = { actorSub: sub, target };
          writeViewAs(next);
          setChoice(next);
          setViewAsError(null);
        }}
      />
    );
  }

  if (!canLogin) {
    return <div>{NOT_ALLOWED}</div>;
  }

  if (!ownMe.rep_id) {
    return (
      <LoadingAbortPaper>
        <Typography>{NOT_SET_UP}</Typography>
        <InternalLink to="/logout">Logout and Start Over</InternalLink>
      </LoadingAbortPaper>
    );
  }

  return (
    <ViewAsContext.Provider value={{ readOnly: false, canViewAs, me: null }}>
      <Outlet context={{ api: ownApi, me: ownMe }} />
    </ViewAsContext.Provider>
  );
};

export default LoginGuard;
